<script setup>
import {onMounted, reactive, ref} from "vue";
import { url } from '@/config/urls';
import router from "@/router/index.js";
import axios from "axios";
import Password from '@parts/Password.vue'
import { useAuthStore } from "@stores/auth";

// コンポーネントに渡されたpropsの取得
const props = defineProps({
  customerUnityCode: {type: String, default: ''},
  password: {type: String, default: ''},
  email: {type: String, default: ''},
  passwordError: {type: String, default: ''},
  emailError: {type: String, default: ''},
  loginUrl: {type: String, default: url.login},
  changePasswordPageUrl: {type: String, default: url.api.changePassword},
  errorPageUrl: {type: String, default: url.error},
});

const customerUnityCode = ref(props.customerUnityCode);
/** メール未登録（初回登録）かどうか。API取得完了まで null */
const isForcedChangeEmail = ref(null);
/** トークン情報の取得完了フラグ（未取得時はフォーム非表示） */
const isReady = ref(false);
const isSubmitting = ref(false);
const generalErrorMessage = ref('');
const input = reactive({
  newPassword: props.password,
  confirmPassword: '',
  email: props.email
});
const isFinishReadingTerms = ref(false);
const isAcceptedTerms = ref(false);
const errorMessage = reactive({
  newPassword: props.passwordError,
  confirmPassword: '',
  email: props.emailError
});

/**
 * 強制パスワード変更トークンから画面表示用情報を適用する
 * @param {object} tokenData
 * @returns {boolean} 適用できた場合 true
 */
const applyTokenData = (tokenData) => {
  if (!tokenData?.customerUnityCode || typeof tokenData.isForcedChangeEmail !== 'boolean') {
    return false;
  }
  customerUnityCode.value = tokenData.customerUnityCode;
  isForcedChangeEmail.value = tokenData.isForcedChangeEmail;
  isReady.value = true;
  return true;
};

onMounted(async() => {
  try {
    // sessionStorageからトークン情報を取得する
    const storedToken = sessionStorage.getItem('FORCED_PASSWORD_CHANGE_TOKEN');

    if (storedToken) {
      const data = JSON.parse(storedToken);
      await axios.post(url.api.changePasswordSetToken, {
        token: data.token
      }, {
        withCredentials: true
      });
      sessionStorage.removeItem('FORCED_PASSWORD_CHANGE_TOKEN');
    }

    // セッション上のトークンから isForcedChangeEmail を取得（表示モードの正）
    const res = await axios.get(url.api.changePasswordGetToken, {
      withCredentials: true
    });
    if (!applyTokenData(res.data)) {
      router.replace(props.errorPageUrl);
    }
  } catch(e) {
    router.replace(props.errorPageUrl);
  }
});

const terms = `『共創未来ポータル』利用規約

この利用規約（以下「本規約」という。）は、東邦薬品株式会社（以下「当社」という。）が提供する『共創未来ポータル』（以下「本アプリケーション」という。）に関する利用条件を定めるものです。本アプリケーションをご利用のお客様（以下「利用者」という。）は、本規約に同意のうえ、本規約の定めに従って利用するものとします。

第１章　総則

第１条（同意と利用契約）
利用者は、本アプリケーションの利用を希望する場合、本規約が利用者と当社との間の、本アプリケーションの利用に関わる利用契約であることを理解し、本規約に同意のうえ本アプリケーションを利用するものとします。

第２条（本規約の変更）
　１．本規約の内容は、当社が必要と認めた場合、変更できるものとします。
　２．変更後の本規約は、本アプリケーションの「お知らせ」画面上で利用者へ通知します。なお、この場合、通知に記載されている適用日から変更後の本規約の効力が発生するものとします。

第３条（定義）
本規約において使用する用語は、次の各号に定める意味を有するものとします。
　①利用者設備
　　本アプリケーションの提供を受けるため利用者が設置するコンピュータ、電気通信設備その他の機器及びソフトウェア
　②本アプリケーション用サーバー設備
　　本アプリケーションを提供するにあたり、当社が指定する業者が設置するコンピュータ、電気通信設備その他の機器及びソフトウェア
　③電気通信回線
　　本アプリケーション及び本アプリケーション用サーバー設備を提供するために、当社が電気通信事業者より借り受ける電気通信回線

第４条（権利義務の譲渡の禁止）
利用者は、当社の書面による事前の承諾なく、本規約に基づく権利若しくは義務を第三者に譲渡し、又は担保に供することはできないものとします。

第２章　登録と契約解除等

第５条（登録及び登録変更）
　１．利用者は、当社の定める方法によって本アプリケーション利用に関する登録申請を行います。
　２．当社は、利用者に次の各号のいずれかの事由があると判断した場合、登録を承諾しないことがあり、その理由について一切開示義務を負いません。
　　①登録の申請に際して虚偽の事項を届け出た場合
　　②本規約に違反したことがある者からの申請である場合
　　③その他、登録が適当でないと当社が判断した場合
　３．利用者は、登録事項に変更があった場合、当社の定める方法によりすみやかに当社に通知するものとします。

第６条（ＩＤ、仮パスワードの発行及び管理）　
　１．当社は、利用者に対し、ID、仮パスワードを発行します。利用者は、利用者自身でID、仮パスワードを用いて本アプリケーション利用に必要な登録および本パスワードを設定します。
　２．利用者は、自己の責任において適切にＩＤ、パスワードを管理するものとし、第三者に譲渡又は貸与してはならず、ＩＤパスワードの組み合わせが登録情報と一致してログインされた場合には、当該ＩＤを登録している利用者自身による利用とみなします。
　３．ＩＤ、パスワードの管理不十分、利用上の過誤、第三者の利用等によって生じた損害に関する全ての責任は利用者が負うものとします。ただし、当社の故意又は過失によりユーザーＩＤ、パスワードが第三者に利用された場合は、この限りではありません。

第７条（利用者からの利用契約の解約)\t　　　　　
利用者は、利用契約の解約を希望する場合は、当社所定の方法をもって当社に通知することにより、利用契約を解約することができます｡この場合、解約希望日の到来をもって、利用契約は解約されます。

第８条（当社からの利用契約の解除)
　１．当社は、利用者が次の各号のいずれかに該当した場合、利用者へ何等の通知等を要することなく利用契約の全部又は一部を解除することができます。
　　①利用申込書その他通知内容が虚偽又は不十分であった場合
　　②自ら振出し若しくは引受けた手形若しくは小切手が不渡りとなった場合、又は支払停止若しくは支払不能となった場合
　　③差押、仮差押、仮処分若しくは競売の申立てを受け、又は公租公課の滞納処分を受ける等、事業の継続が著しく困難になったと認められる場合
　　④破産手続開始、再生手続開始、更生手続開始又は特別清算開始の申立てがあった場合
　　⑤清算の開始をしたとき、又は会社の重要な事業を他に譲渡した場合
　　⑥利用契約に違反し、当社がかかる違反の是正を催告した後、合理的な期間内に是正されない場合
　　⑦監督官庁から営業許可の取消、停止等の処分を受けた場合
　　⑧利用環境の整備が困難であると判断された場合
　　⑨利用契約を履行することが困難となる事由が生じた場合
　　⑩当社からの連絡に対して30日間応答がないとき
　　⑪その他当社が不適当と判断した場合
　２．前項の各号に該当し、契約が解除された利用者は、直ちに期限の利益を喪失し、利用契約から生じるすべての債務を履行しなければなりません。

第９条（利用契約終了後の処理）
当社は、利用契約が終了した場合、本アプリケーション用サーバー設備に記録された情報については、当社の責任で消去します。

第３章　サービス

第１０条（本アプリケーションの内容）
　１．当社が提供する本アプリケーションの内容は、次の各号に定めるとおりとします。なお、本アプリケーションの提供区域は、日本国内に限定されます。
　　①発注した商品の欠品情報表示
　　②欠品した商品の同一成分の検索
　　③独立行政法人医薬品医療機器総合機構（Pharmaceuticals and Medical Devices Agency 以下「PMDA」という。）のウェブサイト上に記載されたリダイレクトページのURLを利用した添付文書の出力。
　２．本アプリケーション上でのデータ保管期間は、当該データを受注した日から90日間とします。　
　３．当社が行う本アプリケーションの導入手続きは、次の各号に定めるとおりです。
　　①当社が管理するサーバーと本アプリケーションに対する利用申込内容の登録
　　②第６条に定める利用者に対するユーザーＩＤ、仮パスワードの発行
　４．当社は、次の各号に定めるものについては、利用契約において別段の定めがある場合を除き、利用者へ提供しません。
　　①利用者が用意したハードウェアに関する問い合わせ並びに障害対応等
　　②用紙等の消耗品の供給
　　③本アプリケーションを構成するデータの内容及び変更等に関する問合せへの回答

第１１条（動作環境）
　１．利用者は、レセプトコンピュータ等のVPN回線につながる専用端末を除くコンピュータ端末で、本アプリケーションを利用するものとします。
　２．本アプリケーションは、以下のコンピュータ端末の動作環境で利用するものとします。
　　【パソコン】\t
　　　OS：Windows10/ 11　　ブラウザ：Chrome 、Edge 、Firefox
　　　OS：macOS 14　　ブラウザ：Safari
　　【スマートフォン】
　　　OS：iOS16/ 17　　ブラウザ：Safari、Chrome
　　　OS：Android10/11/12/ 13\tブラウザ：Chrome
　　・適切なセキュリティ対策を行うこと
　　・通信機器、ＬＡＮ（無線ＬＡＮを含む。）
　　・電気通信回線：ＡＤＳＬ回線以上
　３．利用者は、本アプリケーションを利用するため、自己の責任と費用で、電気通信事業者等の電気通信サービスを利用して利用者設備をインターネットに接続し、不正侵入の防止等、適切な措置を講ずるものとします。
　４．当社は、利用者設備及び前項のインターネット接続、並びに本アプリケーション利用のための環境に不具合がある場合は、利用者に対する本アプリケーションの提供義務を負わないものとします。

第１２条（本アプリケーション提供環境・運用に係る前提条件）
　１．本アプリケーションの提供に係る受託情報、プログラム等の保存、及びこれらに関する電気通信回線の機器類の設置については、当社が委託する【Amazon Web Services】にて行います。
　２．当社は、本アプリケーションの提供に係る受託情報、プログラム等の保存及びこれらに関するサーバー等の機器類は、日本国の法令の適用が及ぶ場所に設置します。
　３．当社は、本アプリケーション運営上、データセンター等での機器や通信回線の増強、運用に係るプログラムの改善等を目的とし、必要最小限の範囲で、受託された情報の利用状況（例えばハードディスク容量、データへのアクセス状況、回線のトラフィック等）に関する統計データの取得を行います。
　４．当社は、本アプリケーション提供に際し、個別の障害対応等に際して、受託された医療情報を、利用者との事前の合意に基づき参照することがあります。また、セキュリティ対応上、必要と考えられる受託情報へのアクセス状況やシステム負荷の状況等を統計化することがあります。

第１３条（本アプリケーションの利用）
利用者は、本アプリケーションを本アプリケーション用サーバー設備上で動作させ、インターネットを経由して利用することができます。なお、本アプリケーション利用可能時間は、当社が定めるメンテナンス及び障害時を除き、24時間365日とします。

第１４条（本アプリケーションの中断及び提供停止）
　１．当社は、次の各号のいずれかに該当する場合には、利用者への事前の通知又は利用者の承諾を要することなく、本アプリケーションの提供を中断することができます。
　　①本アプリケーション用サーバー設備の故障により保守を行う場合
　　②本アプリケーション用サーバー設備の定期点検を行う場合
　　③運用上又は技術上の理由でやむを得ない場合
　　④天災、地変、騒乱、暴動等の不可抗力により本アプリケーションを提供できない場合
　２．当社は、利用者が次の各号のいずれかに該当する場合には、利用者への事前の通知若しくは催告を要することなく本アプリケーションの全部又は一部の提供を停止することができるものとします。
　　①第８条（当社からの利用契約の解除）各号のいずれかに該当する場合
　　②第１８条（禁止事項）に違反した場合
　　③本規約に違反した場合
　３.当社は、本アプリケーションが利用できないことに起因して利用者に生じた損害について、一切の責任を負わないものとします。

第１５条（本アプリケーションの廃止）
当社は、次の各号のいずれかに該当する場合、本アプリケーションの全部又は一部を廃止し、廃止日をもって利用契約の全部又は一部を解約することができます。
　①廃止日の1ヶ月前までに利用者に通知した場合
　②天災地変等不可抗力により本アプリケーションを提供できない場合

第１６条（本アプリケーションの保守及び不具合への対応）
　１．当社は、毎月第二日曜日に本アプリケーションの定期保守メンテナンスを行います。その際、本アプリケーションのご利用はできません。
　２．当社及び利用者は、本アプリケーションに不具合が発生したときは、対応作業を実施します。

第４章　利用者と当社の責務

第１７条（権利の帰属）
　１．本アプリケーションに関する知的財産権等一切の権利は、当社に帰属するものとします。
　２．第１０条第１項第3号に定める添付文書に記載されている対象商品に関する知的財産権等は、対象商品情報をPMDA に登録している事業者、PMDA 又は厚生労働省に帰属します。

第１８条（禁止事項）
　１．利用者は、本アプリケーションの利用にあたり、次の各号のいずれかに該当する行為又は該当すると当社が判断する行為をしてはなりません。
　　①法令又は公序良俗に違反する行為
　　②犯罪行為に関連する行為
　　③当社のサーバー又はネットワークの機能を破壊したり、妨害したりする行為
　　④当社のサービスの運営を妨害するおそれのある行為
　　⑤他の利用者に成りすます行為
　　⑥当社のサービスに関連して、反社会的勢力に対して直接又は間接に利益を供与する行為
　　⑦第三者に成りすまして本アプリケーションを利用する行為
　　⑧第三者の設備又は本アプリケーション用サーバー設備の利用若しくは運営に支障を与える行為、又は与える恐れのある行為
　　⑨第１７条に定める本アプリケーションを提供するに係る、当社又は第三者の書作物などの無断利用
　　⑩その他、当社が不適切と判断する行為
　２．利用者は、前項各号のいずれかに該当する行為がなされたことを知った場合、又は該当する行為がなされるおそれがあると判断した場合は、直ちに当社に通知するものとします。


第１９条（善管注意義務）
当社は、本アプリケーションの利用期間中、善良なる管理者の注意をもって本アプリケーションを提供するものとします。

第２０条（データの調査）
当社は、当社が本アプリケーションに関して保守、運用上又は技術上必要であると判断した場合、利用者が本アプリケーションにおいて提供、伝送するデータ等について、監視、分析、調査等必要な行為を行うことができます。

第２１条（本アプリケーション用サーバー設備の障害）
　１．当社は、本アプリケーション用サーバー設備について障害があることを知ったときは、すみやかに利用者にその旨を通知します。
　２．当社は、当社指定業者が設置した本アプリケーション用サーバー設備に障害があることを知ったときは、すみやかに本アプリケーション用サーバー設備を修理又は復旧するよう努めます。
　３．当社は、電気通信回線について障害があることを知ったときは、当該回線を提供する事業者に修理又は復旧を指示します。

第２２条（再委託）
当社は、利用者に対する本アプリケーションの提供に関して、必要となる業務の全部又は一部を第三者に再委託することができます。この場合、当社は、当該再委託先（以下「再委託先」という。）に対し、第２４条（秘密情報及び個人情報の取扱い）のほか、当該再委託業務を遂行する範囲において必要な義務を負わせます。

第２３条（本アプリケーションの変更）
本アプリケーションは、当社が必要と認めた場合、利用者への事前の通知又は利用者の承諾を要せず、その内容を変更することができます。これによって利用者が被った損害について、当社は一切の責任を負わないものとします。

第２４条（秘密情報）
　１．当社及び利用者は、本アプリケーション利用のため、相手方より提供を受けた技術上又は営業上その他業務上の情報（以下「秘密情報」という。）を、相手方の書面による事前承諾を得ることなく第三者に開示又は漏洩しません。ただし、次の各号のいずれかに該当する情報についてはこの限りではありません。
　　①秘密保持義務を負うことなく既に保有している情報
　　②秘密保持義務を負うことなく第三者から正当に入手した情報
　　③相手方から提供を受けた情報によらず、独自に開発した情報
　　④利用契約に違反することなく、かつ、受領の前後を問わず公知公用となった情報
　２．当社及び利用者は、相手方に書面により事前に通知したうえで、国、地方公共団体等の公的機関又は法令例規の要請にしたがって、必要最小限の範囲で秘密情報を当該公的機関に開示し、又は公表することができます。
　３．第１項乃至第６項の規定にかかわらず、当社が必要と認めた場合には、再委託先に対して、委託のために必要な範囲で、利用者から事前の書面による承諾を得ることなく秘密情報を開示することができます。ただしこの場合、当社は再委託先に対して、本条に基づき当社が負う秘密保持義務と同等の義務を負わせます。
　４．秘密情報の提供を受けた当事者は、当該秘密情報の管理に必要な措置を講じます。
　５．秘密情報の提供を受けた当事者は、相手方より提供を受けた秘密情報を利用契約に定める本アプリケーションの提供又は利用に必要な範囲内でのみ利用します。
　６．当社は、本目的の達成に必要な範囲内で、秘密情報を複製又は改変（以下本項においてあわせて「複製等」という｡）することができます。この場合、当社は、当該複製等された秘密情報についても、本条に定める秘密情報として取り扱うものとします。
　７．利用者は、秘密情報の複製等をすることができないものとします。
　８．本条の規定は、利用契約終了後も３年間は有効に存続します。

第２５条 (個人情報の取扱い)
　１．当社は、利用者から提供された個人情報を、当社のプライバシーポリ（https://www.tohoyk.co.jp/privacy-policy/）、各種法令、ガイドラインに則り取り扱うものとします。
　２．当社は、前項に定める個人情報を、次の各号に定める利用目的の範囲内で利用します。
　　① 本サービスの会員登録および会員認証
　　②DM、各種お知らせ等の配信・送付
　　③本サービスの改善・新規サービスの開発およびマーケティング
　　④お寄せいただいたご意見、お問い合わせ内容の確認・回答
　　⑤本サービスに関するキャンペーン、アンケート、モニター、取材等の実施および回答結果の分析
　　⑥利用者に適した商品、各種サービス等の案内、提供

第５章　損害賠償等

第２６条（非保証・免責事項）
　１．当社は、利用者に対して、以下の各号の事項について、一切の保証をしません。
　　①本アプリケーションの内容について、その完全性、正確性及び有効性等
　　②本アプリケーションに中断、中止その他の障害が生じないこと
　２．当社は、次の各号のいずれかに該当する事由により、利用者及び第三者に発生した損害については、債務不履行責任、不法行為責任、その他法律上の請求原因の如何を問わず、一切の賠償の責任を負わないものとします。
　　①第５条（登録及び登録変更）第２項各号のいずれかに該当すると当社が判断したことによる、利用契約の不成立又は終了
　　②利用者が第５条（登録変更及び登録変更）第３項　に規定する登録変更の手続きを怠った場合、又は登録変更に係る通知が当社へ到達しなかった場合
　　③第８条（当社からの利用契約の解除）第１項各号に基づく当社による利用契約の解除
　　④利用者の、ユーザーＩＤ及びパスワードの管理不徹底
　　⑤利用者設備の障害若しくは外的要因により発生したデータ破損等、又は本アプリケーション用サーバー設備までのインターネット接続サービスの不具合等、利用者の接続環境の障害
　　⑥本アプリケーション用サーバー設備からの応答時間等、インターネット接続サービスの性能値に起因する損害
　　⑦当社が、第三者から導入しているコンピュータウィルス対策ソフトについて当該第三者からウィルスパターン、ウィルス定義ファイル等を提供されていない種類の、コンピュータウィルスによる本アプリケーション用サーバー設備への侵入
　　⑧当社が定める手順・セキュリティ手段等を利用者等が遵守しないことに起因して発生した損害
　　⑨本アプリケーション用サーバー設備のうち、当社の製造に係らないソフトウェア(ＯＳ、ミドルウェア、ＤＢＭＳ)、ハードウェア及びデータベースに起因して発生した損害
　　⑩電気通信回線の不具合に起因して発生した損害
　　⑪第１８条（禁止事項）の違反
　　⑫利用者と金融機関の間の利用料金等の決済をめぐった紛争
　　⑬再委託先の選任・監督につき当社に過失などの帰責事由がない場合の、再委託先の業務に関すること
　　⑭天災、地変、火災、ストライキ、通商停止、戦争、内乱、疫病・感染症の流行その他の不可抗力により利用契約の全部又は一部に不履行が発生した場合、利用者に生じた損害

第２７条（損害賠償）
　１．利用者は、利用者及び利用者の従業員又は利用者の施設を利用する患者等が、その故意又は過失により当社に損害を与えた場合、当社に対して、当該損害の賠償を行います。
　２．当社は、当社の帰責事由により利用者に損害を与えた場合、現実かつ直接に発生した通常の損害の範囲内でその損害の賠償を行います。

第２８条（裁判管轄）
当社と利用者との間で本アプリケーションの利用に関して紛争が生じた場合、東京地方裁判所を第一審の専属的合意管轄裁判所とします。


以上

2024年2月13日　制定
2024年2月21日　改定`;

// バリデーションチェック
const validateNewPassword = (newPassword) => {
  if (!newPassword) {
    errorMessage.newPassword = '新しいパスワードを入力してください。';
  } else if (newPassword.length < 8) {
    errorMessage.newPassword = '新しいパスワードは8文字以上で入力してください。';
  } else if (newPassword.length > 255) {
    errorMessage.newPassword = '新しいパスワードは255文字以内で入力してください。';
  } else if (
      !newPassword.match(/^[a-zA-Z0-9^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]{8,255}$/)) {
    errorMessage.newPassword = 'パスワードに使用できない文字が含まれています。';
  } else if (!newPassword.match(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).*$/)) {
    errorMessage.newPassword = '英字大文字、英字小文字、数字、いずれも含むパスワードを設定してください。';
  } else {
    errorMessage.newPassword = '';
  }
}

const validateConfirmPassword = (newPassword, confirmPassword) => {
  if (!confirmPassword) {
    errorMessage.confirmPassword = '新しいパスワード(確認用)を入力してください。';
  } else if (newPassword !== confirmPassword) {
    errorMessage.confirmPassword = '新しいパスワードと新しいパスワード(確認用)に入力された値が一致していません。';
  } else {
    errorMessage.confirmPassword = '';
  }
}

const validateEmail = (email) => {
  if (!email) {
    errorMessage.email = 'メールアドレスを入力してください。';
  } else if (email.length > 255) {
    errorMessage.email = 'メールアドレスは255文字以内で入力してください。';
  } else if (!email.match(
      /^[a-zA-Z0-9_.+-]+@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/)) {
    errorMessage.email = 'メールアドレスを正しい形式で入力してください。';
  } else {
    errorMessage.email = '';
  }
}

// 利用規約スクロール時
const onScrollCheckbox = (event) => {
  const element = event.target;
  // 最下部までスクロールした場合に「利用規約に同意する」にチェック可能にする
  if (element.scrollTop + element.clientHeight > element.scrollHeight - 75) {
    isFinishReadingTerms.value = true;
  }
}

// 送信ボタン押下時
const onClickSubmitButton = async() => {
  // 二重送信防止
  if (isSubmitting.value) {
    return;
  }
  generalErrorMessage.value = '';
  // バリデーションチェック
  validateNewPassword(input.newPassword);
  validateConfirmPassword(input.newPassword, input.confirmPassword);
  if (isForcedChangeEmail.value) validateEmail(input.email);

  if (errorMessage.newPassword || errorMessage.confirmPassword || errorMessage.email) {
    return;
  }
  isSubmitting.value = true;
  try {
    const response = await axios.post(props.changePasswordPageUrl, {
      password: input.newPassword
      , confirmPassword: input.confirmPassword
      , email: input.email
    }, {
      withCredentials: true
    });
    try {
      const auth = useAuthStore();
      await auth.login({
      loginId: customerUnityCode.value
      , password: input.newPassword
    });
      // 成功 → ユーザー設定画面へ
    await router.push('/setting');
    } catch(e) {
      await router.push(props.loginUrl);
    }
  } catch (e) {
    if (e.response?.data?.message) {
      generalErrorMessage.value = e.response.data.message;
    }
    if (e.response?.data?.errors) {
      e.response.data.errors.forEach(err => {
        switch(err.field) {
          case 'password':
            errorMessage.newPassword = err.message
            break;
          case 'confirmPassword':
            errorMessage.confirmPassword = err.message;
            break;
          case 'email':
            errorMessage.email = err.message
            break;
        }
      })
    }
  } finally {
    isSubmitting.value = false;
  }
}

</script>

<template>
  <template v-if="isReady">
  <h2 class="title_sub1">パスワード変更</h2>
    <form @submit.prevent="onClickSubmitButton" class="form-group">
      <label for="username">ご利用ID</label>
      <input id="username" type="text" :value="customerUnityCode" autocomplete="username"
        readonly class="form-control com_form1 s1">
      <div class="com_mgt15 com_mgt10s">
        <Password id="new-password" v-model="input.newPassword" name="password"
          placeholder="新しいパスワード" autocomplete="new-password"
            :error="errorMessage.newPassword !== ''"/>
      </div>
      <div v-if="errorMessage.newPassword !== ''" class="com_validate_msg1 error">
        {{ errorMessage.newPassword }}
      </div>
      <div class="com_mgt15 com_mgt10s">
        <Password id="confirm-password" v-model="input.confirmPassword"
          placeholder="新しいパスワード（確認用）" autocomplete="new-password"
            :error="errorMessage.confirmPassword !== ''"/>
      </div>
      <div v-if="errorMessage.confirmPassword !== ''" class="com_validate_msg1 error">
        {{ errorMessage.confirmPassword }}
      </div>
      <div v-if="isForcedChangeEmail" class="com_mgt15 com_mgt10s">
        <input type="text" name="email" placeholder="メールアドレス" v-model="input.email"
          :class="{'form-control com_form1 s1' : true, 'error': errorMessage.email !== ''}">
      </div>
      <div v-if="isForcedChangeEmail && errorMessage.email !== ''" class="com_validate_msg1 error">
        {{ errorMessage.email }}
      </div>
      <textarea class="com_terms1 inner1" v-if="isForcedChangeEmail"
        readonly @scroll="onScrollCheckbox" :value="terms"/>
      <label class="com_terms_check1" v-if="isForcedChangeEmail">
        <input type="checkbox" :disabled="!isFinishReadingTerms" v-model="isAcceptedTerms">
        <span>利用規約に同意する</span>
      </label>
      <div v-if="generalErrorMessage !== ''" class="com_validate_msg1 error">
        {{ generalErrorMessage }}
      </div>
      <button type="submit" class="btn com_btn3 cl1" @click="onClickSubmitButton"
        :disabled="(isSubmitting || (!isAcceptedTerms && isForcedChangeEmail))">
      {{ isSubmitting ? '処理中...' : (isForcedChangeEmail ? '申し込み' : '更新') }}
      </button>
      </form>
  </template>
</template>

<style scope>
textarea {
  height: 120px;
  overflow: hidden scroll !important;
  resize: none;
  font-size: 12px;
  line-height: 1.2;
}
</style>